import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line, Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './DeveloperAnalytics.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const DeveloperAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('30');
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameDetails, setGameDetails] = useState(null);

  useEffect(() => {
    fetchDashboardAnalytics();
  }, [period]);

  const fetchDashboardAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/analytics/dashboard?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Analytics data received:', response.data.data);
      console.log('Viewer Demographics:', response.data.data.viewerDemographics);
      console.log('Traffic Sources:', response.data.data.trafficSources);
      setAnalytics(response.data.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGameDetails = async (gameId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/analytics/game/${gameId}?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGameDetails(response.data.data);
    } catch (err) {
      console.error('Failed to load game details:', err);
    }
  };

  const handleGameClick = (gameId) => {
    setSelectedGame(gameId);
    fetchGameDetails(gameId);
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="loading">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const { summary, games, chartData, viewerDemographics, trafficSources } = analytics;

  // Check if there's any demographic data
  const hasDemographicData = (viewerDemographics.regular || 0) + 
                             (viewerDemographics.indie_dev || 0) + 
                             (viewerDemographics.content_creator || 0) + 
                             (viewerDemographics.anonymous || 0) > 0;

  const hasTrafficData = (trafficSources.direct || 0) + 
                         (trafficSources.homepage || 0) + 
                         (trafficSources.search || 0) + 
                         (trafficSources.profile || 0) + 
                         (trafficSources.external || 0) > 0;

  // Chart data for views over time
  const viewsChartData = {
    labels: chartData.map(d => new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Views',
        data: chartData.map(d => d.views || 0),
        borderColor: 'rgb(102, 126, 234)',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  // Viewer demographics pie chart
  const demographicsData = {
    labels: ['Regular Users', 'Indie Devs', 'Content Creators', 'Anonymous'],
    datasets: [
      {
        data: [
          viewerDemographics.regular || 0,
          viewerDemographics.indie_dev || 0,
          viewerDemographics.content_creator || 0,
          viewerDemographics.anonymous || 0
        ],
        backgroundColor: [
          'rgba(102, 126, 234, 0.8)',
          'rgba(255, 182, 72, 0.8)',
          'rgba(72, 207, 173, 0.8)',
          'rgba(158, 158, 158, 0.8)'
        ]
      }
    ]
  };

  // Traffic sources bar chart
  const trafficData = {
    labels: ['Direct', 'Homepage', 'Search', 'Profile', 'External'],
    datasets: [
      {
        label: 'Traffic Sources',
        data: [
          trafficSources.direct || 0,
          trafficSources.homepage || 0,
          trafficSources.search || 0,
          trafficSources.profile || 0,
          trafficSources.external || 0
        ],
        backgroundColor: 'rgba(102, 126, 234, 0.8)'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: 'var(--text-primary)'
        }
      }
    },
    scales: {
      y: {
        ticks: { color: 'var(--text-secondary)' },
        grid: { color: 'var(--border-color)' }
      },
      x: {
        ticks: { color: 'var(--text-secondary)' },
        grid: { color: 'var(--border-color)' }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: 'var(--text-primary)'
        }
      }
    }
  };

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h1>📊 Developer Analytics</h1>
        <p>Track your indie games' performance and audience engagement</p>
      </div>

      {/* Period Selector */}
      <div className="period-selector">
        <button
          className={period === '7' ? 'active' : ''}
          onClick={() => setPeriod('7')}
        >
          7 Days
        </button>
        <button
          className={period === '30' ? 'active' : ''}
          onClick={() => setPeriod('30')}
        >
          30 Days
        </button>
        <button
          className={period === '90' ? 'active' : ''}
          onClick={() => setPeriod('90')}
        >
          90 Days
        </button>
        <button
          className={period === 'all' ? 'active' : ''}
          onClick={() => setPeriod('all')}
        >
          All Time
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card">
          <div className="summary-icon">🎮</div>
          <div className="summary-content">
            <h3>Total Games</h3>
            <p className="summary-value">{summary.totalGames}</p>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">👁️</div>
          <div className="summary-content">
            <h3>Total Views</h3>
            <p className="summary-value">{summary.totalViews.toLocaleString()}</p>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">🔗</div>
          <div className="summary-content">
            <h3>Store Clicks</h3>
            <p className="summary-value">{summary.totalStoreLinkClicks.toLocaleString()}</p>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">⭐</div>
          <div className="summary-content">
            <h3>Avg Rating</h3>
            <p className="summary-value">{summary.averageRating.toFixed(1)}/5</p>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">📝</div>
          <div className="summary-content">
            <h3>Total Reviews</h3>
            <p className="summary-value">{summary.totalReviews}</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        <div className="chart-card">
          <h3>Views Over Time</h3>
          <div className="chart-wrapper">
            <Line data={viewsChartData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-card">
          <h3>Traffic Sources</h3>
          <div className="chart-wrapper">
            {hasTrafficData ? (
              <Bar data={trafficData} options={chartOptions} />
            ) : (
              <div className="no-data-message">
                <p>No traffic data yet. Analytics will appear once users view your games.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Games Performance Table */}
      <div className="games-section">
        <h2>Games Performance</h2>
        <div className="games-table">
          <table>
            <thead>
              <tr>
                <th>Game</th>
                <th>Status</th>
                <th>Views</th>
                <th>Store Clicks</th>
                <th>Rating</th>
                <th>Reviews</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {games.map(game => (
                <tr key={game.gameId}>
                  <td className="game-name">{game.gameName}</td>
                  <td>
                    <span className={`status-badge ${game.isApproved ? 'approved' : 'pending'}`}>
                      {game.isApproved ? '✓ Approved' : '⏳ Pending'}
                    </span>
                  </td>
                  <td>{game.views.toLocaleString()}</td>
                  <td>{game.storeLinkClicks.toLocaleString()}</td>
                  <td>{game.averageRating.toFixed(1)}/5</td>
                  <td>{game.totalReviews}</td>
                  <td>
                    <button
                      className="btn-view-details"
                      onClick={() => handleGameClick(game.gameId)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Game Details Modal */}
      {selectedGame && gameDetails && (
        <div className="modal-overlay" onClick={() => setSelectedGame(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedGame(null)}>×</button>
            <h2>{gameDetails.game.name}</h2>
            
            <div className="game-details-grid">
              <div className="detail-card">
                <h4>Total Views</h4>
                <p className="detail-value">{gameDetails.totals.views.toLocaleString()}</p>
              </div>
              <div className="detail-card">
                <h4>Avg Rating</h4>
                <p className="detail-value">{gameDetails.game.averageRating.toFixed(1)}/5</p>
              </div>
              <div className="detail-card">
                <h4>Total Reviews</h4>
                <p className="detail-value">{gameDetails.game.totalReviews}</p>
              </div>
            </div>

            {Object.keys(gameDetails.totals.storeLinkClicks).length > 0 && (
              <div className="store-clicks-section">
                <h3>Store Link Clicks</h3>
                <div className="store-clicks-list">
                  {Object.entries(gameDetails.totals.storeLinkClicks).map(([platform, clicks]) => (
                    <div key={platform} className="store-click-item">
                      <span className="platform">{platform}</span>
                      <span className="clicks">{clicks} clicks</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {gameDetails.recentReviews.length > 0 && (
              <div className="recent-reviews-section">
                <h3>Recent Reviews</h3>
                <div className="reviews-list">
                  {gameDetails.recentReviews.slice(0, 5).map(review => (
                    <div key={review._id} className="review-item">
                      <div className="review-rating">
                        {'⭐'.repeat(review.rating)}
                      </div>
                      <p className="review-text">{review.reviewText.substring(0, 150)}{review.reviewText.length > 150 ? '...' : ''}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperAnalytics;
